import { kbLog } from '../../utils/ui/keyboardReframeDebug';
import { computeInlineRevealPlan } from '../../utils/ui/inlineRevealPlan';
import { markProgrammaticScroll } from '../../utils/ui/scrollTapGuard';
import { easeKbMotion } from '../../utils/ui/rafScrollTo';
import { findBodyScroller } from './modalHeightFreeze';
import { withoutHeightTransitions } from './modalMeasure';
import { KB_MOTION_MS } from './modalCloseChoreography';
import { KB_SHRUNK_ATTR } from '../../utils/ui/inlineKeyboardRestorePolicy';
import { measureWithoutSpacer } from '../../utils/ui/inlineKeyboardSpacer';

/**
 * BODY SHRINK instead of a spacer — rest cards only (KAN-930 step 3,
 * RD-DEV-025; founder decision 2026-10-22, Option B).
 *
 * A card that RESTS under the header (`data-cvx-kb-rest`, see
 * `useModalTypingRest`) gets the scroll range the inline guard needs by making
 * its BODY shorter, never by padding it: the old spacer showed as a white void
 * under the content and the height freeze + curtain cut the footer in half
 * (device recording, platform "Deactivate company", 2026-10-22).
 *
 * SHAPE RULE (KAN-1181, founder 2026-10-31 — "never weird"): while typing the
 * card is exactly as tall as its content, or shorter by EXACTLY what the
 * keyboard covers; it never grows and never shows a white band. So the final
 * body shrink is E and nothing else (the old max(D, E) cut a card that nothing
 * covered: recovery codes 292 → 262).
 *
 * At the tap (the hook's WINDOW-capture focusin — before the guard and before
 * iOS samples the caret; for a tap-rested card this is the POST-glide
 * geometry, because the glide's counter-transform is only written in rAF):
 *  - E = how far the card's bottom sits below `kbTop − 8` (footer under keys).
 *    E = 0 → nothing is covered: no shrink, no hold, 'none';
 *  - D = the scroll range the guard's reveal is missing
 *    (`computeInlineRevealPlan` with no spacer cap, same predicted keyboard),
 *    capped to E (D' = min(D, E)): a field can only be hidden by what the
 *    shrink hides, so the shrink never exceeds E.
 * D' is applied SYNCHRONOUSLY so the guard finds the range (no spacer, it only
 * sets scrollTop) — but as a HOLD: body `max-height` −D' and the same D' added
 * to its `margin-bottom`, so nothing on screen moves at the tap. Then ONE rAF
 * driver on the keyboard curve (KB_MOTION, `easeKbMotion`) takes the margin
 * back to 0 and the body down to `target − E`: the footer rides up with the
 * keys. A setTimeout backup (KB_MOTION_MS + 100) finishes the drive at e = 1
 * when rAF stalls, so the hold margin can never outlive the drive.
 * A dialog that does not shrink with its body (fixed height) gets its height
 * pinned and written ONCE to the final value; the shell's own
 * `height KB_MOTION` transition carries it (the shell stays the single owner
 * of the dialog `transition` — keyboardMotionOneTransition).
 *
 * The one-beat close (`release`) runs the same driver backwards and writes the
 * body `max-height` AND `scrollTop` in the SAME frame on the SAME curve, so
 * scrollTop never leaves the live range (no clamp, no snap). The last frame
 * puts the original inline strings back and sets scrollTop exactly.
 *
 * Fallback: any measurement that cannot give the range returns 'failed' and
 * puts every inline value back — the caller then runs today's freeze + guard
 * path unchanged. Never scrolls the PANEL (KAN-552): only the body scroller.
 */

export const CARD_BOTTOM_GAP_PX = 8;
/** Never squeeze a body below this — fall back instead. */
export const MIN_BODY_PX = 56;

function reducedMotion() {
  return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)')?.matches);
}

const px = (v) => parseFloat(v) || 0;

/** Put an inline value back exactly ('' removes it — never leaves a stale px). */
function putBack(el, cssName, value) {
  if (value) el.style.setProperty(cssName, value);
  else el.style.removeProperty(cssName);
}

/** The box height `max-height` acts on (border-box vs content-box). */
function boxHeight(el) {
  const cs = window.getComputedStyle(el);
  if (cs.boxSizing === 'border-box') return el.offsetHeight;
  return el.offsetHeight - px(cs.paddingTop) - px(cs.paddingBottom)
    - px(cs.borderTopWidth) - px(cs.borderBottomWidth);
}

/** Real keyboard top (visual viewport bottom) — innerHeight when unknown. */
export function realKeyboardTop() {
  const vv = typeof window !== 'undefined' ? window.visualViewport : null;
  if (vv && vv.height > 0) return (vv.offsetTop || 0) + vv.height;
  return window.innerHeight;
}

/** True when the visual viewport shows a real obscured band — only then may the shrink GROW back to it. */
export function realKeyboardKnown() {
  const vv = typeof window !== 'undefined' ? window.visualViewport : null;
  return !!(vv && vv.height > 0 && window.innerHeight - ((vv.offsetTop || 0) + vv.height) > 40);
}

/**
 * One rAF driver on the keyboard curve. `write(e)` gets the eased progress 0..1.
 * A setTimeout backup (duration + 100ms) forces e = 1 when rAF is throttled or
 * stalled (iOS background): the HOLD margin must never outlive the drive.
 */
export const DRIVE_BACKUP_PAD_MS = 100;
function drive(durationMs, write, onDone) {
  if (reducedMotion() || durationMs <= 0) {
    write(1);
    if (onDone) onDone();
    return () => {};
  }
  const start = Date.now();
  let raf = 0;
  let backup = 0;
  let over = false;
  const end = () => {
    if (over) return;
    over = true;
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
    clearTimeout(backup);
    backup = 0;
  };
  const step = () => {
    if (over) return;
    const p = Math.min(1, (Date.now() - start) / durationMs);
    write(easeKbMotion(p));
    if (p < 1) raf = requestAnimationFrame(step);
    else { end(); if (onDone) onDone(); }
  };
  backup = setTimeout(() => {
    if (over) return;
    end();
    write(1);
    if (onDone) onDone();
  }, durationMs + DRIVE_BACKUP_PAD_MS);
  raf = requestAnimationFrame(step);
  return end;
}

/** The dialog's current translateY (a running FLIP): rects minus it = layout geometry. */
export function dialogTranslateY(el) {
  const t = window.getComputedStyle(el).transform;
  if (!t || t === 'none') return 0;
  const m = /^matrix3d\((.*)\)$/.exec(t);
  if (m) return parseFloat(m[1].split(',')[13]) || 0;
  const m2 = /^matrix\((.*)\)$/.exec(t);
  return m2 ? (parseFloat(m2[1].split(',')[5]) || 0) : 0;
}

export function createBodyShrink(dialogEl, { onRotate } = {}) {
  let s = null; // { scroller, orig, m0, pinned, cur: {max, margin, height}, target }
  // E = 0 but the guard must not add a spacer (the card never grows): a marked scroller, no shrink.
  let noneMark = null;
  let stop = null;
  let width = 0;

  const halt = () => { if (stop) stop(); stop = null; };

  const apply = (v) => {
    s.cur = { ...v };
    s.scroller.style.maxHeight = `${v.max}px`;
    s.scroller.style.marginBottom = `${v.margin}px`;
  };

  const lerp = (a, b, e) => a + (b - a) * e;

  const animateTo = (target) => {
    halt();
    const from = { ...s.cur };
    s.target = { ...target };
    if (s.pinned && target.height != null && target.height !== from.height) {
      dialogEl.style.height = `${target.height}px`; // ONE write; the shell's height transition carries it
      s.cur.height = target.height;
    }
    // A GROWING body shrinks the scroll range: tween scrollTop to the new range on
    // the same curve, so it never leaves the live range (no browser clamp, no snap).
    const top0 = s.scroller.scrollTop;
    const topEnd = target.max > from.max
      ? Math.max(0, Math.min(top0, s.scroller.scrollHeight - target.max)) : top0;
    if (topEnd !== top0) markProgrammaticScroll(s.scroller, 600);
    stop = drive(KB_MOTION_MS, (e) => {
      apply({ max: lerp(from.max, target.max, e), margin: lerp(from.margin, target.margin, e), height: s.cur.height });
      if (topEnd !== top0) s.scroller.scrollTop = lerp(top0, topEnd, e);
    }, () => { stop = null; });
  };

  const onResize = () => {
    if (!s || window.innerWidth === width) return;
    // Rotation changes the size-family cap: release at once, no tween.
    finish(null); // eslint-disable-line no-use-before-define
    if (onRotate) onRotate();
  };

  function restoreStrings() {
    putBack(s.scroller, 'max-height', s.orig.maxHeight);
    putBack(s.scroller, 'margin-bottom', s.orig.marginBottom);
    if (s.pinned) withoutHeightTransitions(dialogEl, () => putBack(dialogEl, 'height', s.orig.height));
  }

  function finish(scrollTo) {
    halt();
    if (noneMark) { noneMark.removeAttribute(KB_SHRUNK_ATTR); noneMark = null; }
    if (!s) return;
    const { scroller } = s;
    if (scrollTo != null) {
      markProgrammaticScroll(scroller, 600);
      scroller.scrollTop = scrollTo; // exact BEFORE the range comes back (no clamp)
    }
    restoreStrings();
    scroller.removeAttribute(KB_SHRUNK_ATTR);
    if (scrollTo != null && scroller.scrollTop !== scrollTo) scroller.scrollTop = scrollTo;
    s = null;
    window.removeEventListener('resize', onResize);
    window.removeEventListener('orientationchange', onResize);
    kbLog('bodyShrinkEnd', {});
  }

  /**
   * At the tap. `kbTop` = the guard's predicted keyboard top (its target);
   * `kbLine` (optional, keyboard already up) = the real band for the footer.
   * The body shrinks by EXACTLY E (what the keyboard covers); the reveal range
   * D is capped to E. E = 0 → 'none' (KAN-1181 shape rule).
   * @returns {'none'|'shrunk'|'failed'}
   */
  function shrinkFor(field, { kbTop, kbLine = kbTop }) {
    const scroller = findBodyScroller(dialogEl, field);
    if (!scroller) return s ? 'none' : 'failed';
    if (s && s.scroller !== scroller) finish(null);
    halt(); // a running open/close driver stops where it is
    const resume = (r) => { if (s) animateTo(s.target); return r; };
    // A re-plan that fails mid-session (keyboard up): keep the shrink values
    // (nothing jumps) but drop the marker for this reveal, so the guard adds
    // its normal spacer instead of landing short. The close folds that spacer
    // in (captureScrollLoan holds it; the choreography's finish releases it).
    const midFail = () => {
      scroller.removeAttribute(KB_SHRUNK_ATTR);
      kbLog('bodyShrinkMidFail', {});
      return resume('failed');
    };
    // A running FLIP (tap-rest glide) transforms the card: read LAYOUT geometry.
    const ty = dialogTranslateY(dialogEl);
    const fr = field.getBoundingClientRect();
    const sr = scroller.getBoundingClientRect();
    const plan = computeInlineRevealPlan({
      fieldTop: fr.top - ty,
      fieldBottom: fr.bottom - ty,
      scrollerTop: sr.top - ty,
      scrollerScrollTop: scroller.scrollTop,
      scrollerMaxScrollTop: scroller.scrollHeight - scroller.clientHeight,
      predictedKeyboardTop: kbTop,
      maxSpacerPx: Infinity,
    });
    const D0 = plan ? plan.spacer : 0;
    const fresh = !s;
    const m0 = fresh ? px(window.getComputedStyle(scroller).marginBottom) : s.m0;
    const box = fresh ? boxHeight(scroller) : s.cur.max;
    const cur = fresh ? { max: box, margin: m0, height: null } : s.cur;
    const target = fresh ? { ...cur } : s.target;
    const dr = dialogEl.getBoundingClientRect();
    // Where the card ends once the in-flight target lands.
    const plannedBottom = (!fresh && s.pinned) ? dr.top - ty + target.height
      : dr.bottom - ty - (cur.max - target.max) - (cur.margin - target.margin);
    const E = Math.max(0, Math.ceil(plannedBottom - (Math.min(kbTop, kbLine) - CARD_BOTTOM_GAP_PX)));
    if (E <= 0) {
      // Nothing is covered: no shrink, no hold. The marker keeps the guard from
      // adding a spacer (the card would GROW); it only sets scrollTop in range.
      if (D0 > 0 && fresh) {
        if (noneMark && noneMark !== scroller) noneMark.removeAttribute(KB_SHRUNK_ATTR);
        scroller.setAttribute(KB_SHRUNK_ATTR, '');
        noneMark = scroller;
      }
      return resume('none');
    }
    // D' = min(D, E), and the hold never squeezes the body under MIN_BODY_PX: a
    // tall card clamps there and the guard reveals the field INSIDE the body.
    const D = Math.max(0, Math.min(D0, E, cur.max - MIN_BODY_PX));
    const holdMax = cur.max - D;
    const finalMax = Math.max(Math.min(MIN_BODY_PX, holdMax), target.max - E);
    const prev = { maxHeight: scroller.style.maxHeight, marginBottom: scroller.style.marginBottom, height: dialogEl.style.height };
    let pinned = !fresh && s.pinned;
    const H0 = dialogEl.offsetHeight;
    if (fresh) {
      // Probe (sync, no paint): does the dialog follow its body?
      scroller.style.maxHeight = `${finalMax}px`;
      pinned = (H0 - dialogEl.offsetHeight) < (box - finalMax) - 1;
    }
    // HOLD: −D' on the body, +D' on its margin → nothing moves at the tap.
    scroller.style.maxHeight = `${holdMax}px`;
    scroller.style.marginBottom = `${cur.margin + D}px`;
    if (fresh && pinned) withoutHeightTransitions(dialogEl, () => { dialogEl.style.height = `${H0}px`; });
    const range = scroller.scrollHeight - scroller.clientHeight;
    // Only a FULL D must find its range; a capped D' scrolls as far as it can.
    if (plan && D >= D0 && D0 > 0 && range + 1 < plan.scrollTop) {
      // Could not obtain the range → put every value back; caller falls back.
      putBack(scroller, 'max-height', prev.maxHeight);
      putBack(scroller, 'margin-bottom', prev.marginBottom);
      if (fresh && pinned) withoutHeightTransitions(dialogEl, () => putBack(dialogEl, 'height', prev.height));
      kbLog('bodyShrinkFail', { range: Math.round(range), target: plan.scrollTop });
      return fresh ? 'failed' : midFail();
    }
    if (noneMark === scroller) noneMark = null; // a real shrink takes over the marker (finish removes it)
    if (fresh) {
      s = { scroller, orig: prev, m0, pinned, box0: box };
      width = window.innerWidth;
      window.addEventListener('resize', onResize);
      window.addEventListener('orientationchange', onResize);
    }
    // The guard refuses a spacer ONLY on a marked scroller (KAN-930 fallback);
    // re-marked on every successful plan (a mid-session failure removed it).
    scroller.setAttribute(KB_SHRUNK_ATTR, '');
    const baseHeight = fresh ? H0 : target.height;
    s.cur = { max: holdMax, margin: cur.margin + D, height: pinned ? (fresh ? H0 : cur.height) : null };
    s.target = s.cur;
    kbLog('bodyShrink', { D, E, pinned: pinned ? 1 : 0, max: Math.round(finalMax) });
    animateTo({
      max: finalMax,
      margin: m0,
      height: pinned ? Math.round(baseHeight - (target.max - finalMax)) : null,
    });
    return 'shrunk';
  }

  return {
    isActive: () => s != null || noneMark != null,
    owns: (scroller) => !!s && s.scroller === scroller,
    shrinkFor,
    /**
     * Keyboard open: re-plan to EXACTLY E against the REAL band. Shrinks more
     * when the card is under it and — when `grow` (the band is trustworthy) —
     * grows back when it was over-shrunk from the default prediction; never
     * above the natural body, never under MIN_BODY_PX. Tweened on KB_MOTION.
     */
    settleTo(kbLine, { grow = true } = {}) {
      if (!s) return;
      const top = dialogEl.getBoundingClientRect().top - dialogTranslateY(dialogEl); // layout top (a running tap-rest FLIP is only paint)
      const plannedH = s.pinned ? s.target.height
        : dialogEl.offsetHeight - (s.cur.max - s.target.max) - (s.cur.margin - s.target.margin);
      const delta = Math.ceil(top + plannedH - (kbLine - CARD_BOTTOM_GAP_PX)); // > 0 under the band, < 0 room to spare
      if (delta === 0 || (delta < 0 && !grow)) return;
      const max = Math.min(s.box0, Math.max(MIN_BODY_PX, s.target.max - delta));
      const applied = s.target.max - max;
      if (applied === 0) return;
      kbLog('bodyShrinkSettle', { delta, applied });
      animateTo({ ...s.target, max, height: s.pinned ? s.target.height - applied : null });
    },
    /** Shrink-only twin (the band is not trustworthy for growing). */
    clampTo(kbLine) { this.settleTo(kbLine, { grow: false }); },
    /**
     * The dialog's height with every inline shrink value put back (sync, no
     * paint) — where the close glide must centre a tap-rested card. null = no shrink.
     */
    naturalDialogHeight() {
      if (!s) return null;
      const { scroller } = s;
      const now = { maxHeight: scroller.style.maxHeight, marginBottom: scroller.style.marginBottom, height: dialogEl.style.height };
      let h = null;
      measureWithoutSpacer(() => withoutHeightTransitions(dialogEl, () => {
        putBack(scroller, 'max-height', s.orig.maxHeight);
        putBack(scroller, 'margin-bottom', s.orig.marginBottom);
        if (s.pinned) putBack(dialogEl, 'height', s.orig.height);
        h = dialogEl.offsetHeight;
        putBack(scroller, 'max-height', now.maxHeight);
        putBack(scroller, 'margin-bottom', now.marginBottom);
        if (s.pinned) putBack(dialogEl, 'height', now.height);
      }));
      return h;
    },
    /**
     * One-beat close: body max-height, margin AND scrollTop on one curve, one
     * frame each. `scrollTo` null = leave scroll alone. Ends with `finish`,
     * unless `keep` — then the values stay applied (at their natural sizes)
     * until the caller's `finish`, so `resumeTyping` can still bring the
     * typing geometry back (KAN-561: the keyboard may return before the blur).
     */
    release({ scrollTo = null, onDone, keep = false } = {}) {
      if (!s) { if (onDone) onDone(); return; }
      halt();
      const { scroller } = s;
      // Natural sizes, read with the original strings (sync, no paint).
      const now = { maxHeight: scroller.style.maxHeight, marginBottom: scroller.style.marginBottom, height: dialogEl.style.height };
      let natural = 0;
      let naturalH = null;
      // Without a guard spacer folded in mid-session: its padding would inflate
      // the natural size, and finish's release would then snap it (one beat).
      measureWithoutSpacer(() => withoutHeightTransitions(dialogEl, () => {
        putBack(scroller, 'max-height', s.orig.maxHeight);
        putBack(scroller, 'margin-bottom', s.orig.marginBottom);
        if (s.pinned) putBack(dialogEl, 'height', s.orig.height);
        natural = boxHeight(scroller);
        if (s.pinned) naturalH = dialogEl.offsetHeight;
        putBack(scroller, 'max-height', now.maxHeight);
        putBack(scroller, 'margin-bottom', now.marginBottom);
        if (s.pinned) putBack(dialogEl, 'height', now.height);
      }));
      const from = { ...s.cur, top: scroller.scrollTop };
      s.typing = { ...from };
      if (s.pinned && naturalH != null) { dialogEl.style.height = `${naturalH}px`; s.cur.height = naturalH; }
      if (scrollTo != null) markProgrammaticScroll(scroller, 600);
      stop = drive(KB_MOTION_MS, (e) => {
        apply({ max: lerp(from.max, natural, e), margin: lerp(from.margin, s.m0, e), height: s.cur.height });
        if (scrollTo != null) scroller.scrollTop = lerp(from.top, scrollTo, e);
      }, () => { stop = null; if (!keep) finish(scrollTo); if (onDone) onDone(); });
    },
    /**
     * The keyboard came back before the close finished (KAN-561): run the
     * same driver back to the typing geometry (body, margin AND scrollTop on
     * one curve). Returns false when there is no release to undo.
     */
    resumeTyping() {
      if (!s || !s.typing) return false;
      halt();
      const { scroller } = s;
      const to = s.typing;
      s.typing = null;
      const from = { ...s.cur, top: scroller.scrollTop };
      if (s.pinned && to.height != null) { dialogEl.style.height = `${to.height}px`; s.cur.height = to.height; }
      s.target = { max: to.max, margin: to.margin, height: to.height };
      markProgrammaticScroll(scroller, 600);
      kbLog('bodyShrinkResume', {});
      stop = drive(KB_MOTION_MS, (e) => {
        apply({ max: lerp(from.max, to.max, e), margin: lerp(from.margin, to.margin, e), height: s.cur.height });
        scroller.scrollTop = lerp(from.top, to.top, e);
      }, () => { stop = null; });
      return true;
    },
    /** A re-tap mid-close: stop where it is (values stay); the tap re-plans. */
    cancelRelease() {
      halt();
      if (s) { s.target = { ...s.cur }; s.typing = null; }
    },
    finish,
    dispose() { finish(null); },
  };
}
